import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import ButtonGroup from '@atlaskit/button/button-group';
import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import RecentIcon from '@atlaskit/icon/glyph/recent';
import ModalDialog, {
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalBody,
} from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';

import { useIntl } from 'src/hooks/intl';
import { commonMessages } from 'src/i18n';
import globalUrls from 'src/urls/global';

import messages from './merge-dialog.i18n';

type WarningMergeDialogProps = {
  createPendingMerge: () => void;
  onHideDialog: () => void;
};

const WarningMergeDialog = ({
  createPendingMerge,
  onHideDialog,
}: WarningMergeDialogProps) => {
  const intl = useIntl();

  const Footer = useCallback(
    () => (
      <ModalFooter>
        <Box xcss={xcss({ paddingInlineEnd: 'space.400' })}>
          <CustomThemeButton
            appearance="link"
            href={globalUrls.external.mergeChecks}
            target="_blank"
            label={intl.formatMessage(messages.mergeWarningLinkText)}
          >
            <FormattedMessage {...messages.mergeWarningLinkText} />
          </CustomThemeButton>
        </Box>
        <ButtonGroup>
          <CustomThemeButton
            appearance="warning"
            label={intl.formatMessage(messages.createPendingMergeAction)}
            iconBefore={<RecentIcon size="small" label="" />}
            onClick={createPendingMerge}
          >
            <FormattedMessage {...messages.mergeWarningMergeAnywayButton} />
          </CustomThemeButton>
          <CustomThemeButton
            appearance="subtle"
            onClick={onHideDialog}
            label={intl.formatMessage(commonMessages.cancel)}
          >
            <FormattedMessage {...commonMessages.cancel} />
          </CustomThemeButton>
        </ButtonGroup>
      </ModalFooter>
    ),
    [createPendingMerge, onHideDialog, intl]
  );

  return (
    <ModalDialog onClose={onHideDialog} width="small">
      <ModalHeader>
        <ModalTitle appearance="warning">
          <FormattedMessage {...messages.mergeWarningDialogTitle} />
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <FormattedMessage {...messages.mergeWarningDialogDescription} />
      </ModalBody>
      <Footer />
    </ModalDialog>
  );
};

export default React.memo(WarningMergeDialog);
